<template>
    <v-dialog :model-value="modelValue" persistent width="520"
              @update:model-value="$emit('update:modelValue', $event)">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ session ? 'Edit coaching note' : 'New coaching note' }}</span>
                <v-btn icon="mdi-close" variant="text" density="compact" @click="close"/>
            </v-card-title>

            <v-form v-model="valid" @submit.prevent="save">
                <v-card-text>
                    <v-text-field v-model="form.topic" :rules="[v => !!v || 'Topic is required']"
                                  density="compact" label="Topic *" autofocus/>
                    <v-select v-model="form.severity" :items="severityOptions" item-title="label" item-value="value"
                              density="compact" label="Severity" clearable/>
                    <v-textarea v-model="form.notes" density="compact" rows="3" label="Notes"/>
                    <v-alert v-if="tripId" type="info" variant="tonal" density="compact" class="mt-1">
                        Linked to trip #{{ tripId }}
                    </v-alert>
                </v-card-text>

                <v-card-actions class="px-4 pb-3 d-flex justify-end">
                    <v-btn variant="text" @click="close">Cancel</v-btn>
                    <v-btn color="primary" type="submit" :loading="saving" :disabled="!valid">Save</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {reactive, ref} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const props = defineProps({
    modelValue: Boolean,
    driverId: {type: [String, Number], required: true},
    session: {type: Object, default: null},
    tripId: {type: [String, Number], default: null},
});
const emit = defineEmits(["update:modelValue", "saved"]);
const {showSnackbar} = useSnackbarStore();

const severityOptions = [
    {value: "low", label: "Low"},
    {value: "medium", label: "Medium"},
    {value: "high", label: "High"},
];

const valid = ref(false);
const saving = ref(false);
const form = reactive({
    topic: props.session?.topic ?? null,
    severity: props.session?.severity ?? null,
    notes: props.session?.notes ?? null,
});

const close = () => emit("update:modelValue", false);

const save = () => {
    if (!valid.value) return;
    saving.value = true;

    const payload = {
        topic: form.topic,
        severity: form.severity ?? "",
        notes: form.notes ?? "",
    };
    if (!props.session && props.tripId) payload.trip_id = props.tripId;

    const base = `companies/drivers/${props.driverId}/coaching`;
    const url = props.session ? `${base}/${props.session.id}` : base;

    send("post", url, payload)
        .then((response) => {
            showSnackbar(response.data.message);
            emit("saved", response.data.data);
            refreshMenuCounts();
            close();
        })
        .catch((error) => {
            const msg = error.response?.data?.errors
                ? Object.values(error.response.data.errors)[0][0]
                : (error.response?.data?.message ?? "Could not save coaching note");
            showSnackbar(msg, "error");
        })
        .finally(() => saving.value = false);
};
</script>
